import { ColorMap } from "@/lib/shaders/shared-color";
import { getColormapDomain, getColormapLightFloor } from "@/lib/shaders/colormap-domain";
import { parseToOklch, oklchToSrgb, clampChroma } from "@/lib/design/oklch";

// Pre-computed color stops for each colormap
// These are carefully chosen to match the actual colormap behavior in shaders

function viridisColor(t: number): [number, number, number] {
  if (t == null || isNaN(t)) t = 0;
  const x = Math.max(0, Math.min(1, t));

  // 6th-order Horner polynomial matching the GLSL implementation (max error ~4/255)
  const c0 = [0.2777273272234177, 0.005407344544966578, 0.3340998053353061];
  const c1 = [0.1050930431085774, 1.404613529898575, 1.384590162594685];
  const c2 = [-0.3308618287255563, 0.214847559468213, 0.09509516302823659];
  const c3 = [-4.634230498983486, -5.799100973351585, -19.33244095627987];
  const c4 = [6.228269936347081, 14.17993336680509, 56.69055260973585];
  const c5 = [4.776384997670288, -13.74514537774601, -65.35303263337234];
  const c6 = [-5.435455855934631, 4.645852612178535, 26.3124352495832];

  const rgb = [0, 1, 2].map(
    (i) =>
      c0[i]! + x * (c1[i]! + x * (c2[i]! + x * (c3[i]! + x * (c4[i]! + x * (c5[i]! + x * c6[i]!)))))
  );

  return [
    Math.round(Math.max(0, Math.min(1, rgb[0]!)) * 255),
    Math.round(Math.max(0, Math.min(1, rgb[1]!)) * 255),
    Math.round(Math.max(0, Math.min(1, rgb[2]!)) * 255),
  ] as [number, number, number];
}

function turboColor(t: number): [number, number, number] {
  if (t == null || isNaN(t)) t = 0;
  const x = Math.max(0, Math.min(1, t));
  const x2 = x * x;
  const x3 = x2 * x;
  const x4 = x3 * x;
  const x5 = x4 * x;

  const r =
    0.13572138 +
    4.6153926 * x -
    42.66032258 * x2 +
    132.13108234 * x3 -
    152.94239396 * x4 +
    59.28637943 * x5;
  const g =
    0.09140261 +
    2.19418839 * x +
    4.84296658 * x2 -
    14.18503333 * x3 +
    4.27729857 * x4 +
    2.82956604 * x5;
  const b =
    0.1066733 +
    12.64194608 * x -
    60.58204836 * x2 +
    110.36276771 * x3 -
    89.90310912 * x4 +
    27.34824973 * x5;

  return [
    Math.round(Math.max(0, Math.min(1, r)) * 255),
    Math.round(Math.max(0, Math.min(1, g)) * 255),
    Math.round(Math.max(0, Math.min(1, b)) * 255),
  ] as [number, number, number];
}

function magmaColor(t: number): [number, number, number] {
  if (t == null || isNaN(t)) t = 0;
  const x = Math.max(0, Math.min(1, t));

  // 6th-order Horner polynomial matching the GLSL implementation
  const c0 = [-0.002136485053939582, -0.000749655052795221, -0.005386127855323933];
  const c1 = [0.2516605407371642, 0.6775232436837668, 2.494026599312351];
  const c2 = [8.353717279216625, -3.577719514958484, 0.3144679030132573];
  const c3 = [-27.66873308576866, 14.26473078053022, -13.64921318813922];
  const c4 = [52.17613981234068, -27.94360607168351, 12.94416944238394];
  const c5 = [-50.76852536473588, 29.04658282127291, 4.23415299384598];
  const c6 = [18.65570506591883, -11.48977351997711, -5.601961508734096];

  const rgb = [0, 1, 2].map(
    (i) =>
      c0[i]! + x * (c1[i]! + x * (c2[i]! + x * (c3[i]! + x * (c4[i]! + x * (c5[i]! + x * c6[i]!)))))
  );

  return [
    Math.round(Math.max(0, Math.min(1, rgb[0]!)) * 255),
    Math.round(Math.max(0, Math.min(1, rgb[1]!)) * 255),
    Math.round(Math.max(0, Math.min(1, rgb[2]!)) * 255),
  ] as [number, number, number];
}

function infernoColor(t: number): [number, number, number] {
  if (t == null || isNaN(t)) t = 0;
  const x = Math.max(0, Math.min(1, t));

  // 6th-order Horner polynomial matching the GLSL implementation
  const c0 = [0.0002189403691192265, 0.001651004631001012, -0.01948089843709184];
  const c1 = [0.1065134194856116, 0.5639564367884091, 3.932712388889277];
  const c2 = [11.60249308247187, -3.972853965665698, -15.9423941062914];
  const c3 = [-41.70399613139459, 17.43639888205313, 44.35414519872813];
  const c4 = [77.162935699427, -33.40235894210092, -58.727762559878];
  const c5 = [-71.31942824499214, 32.62606426397723, 31.92234448290774];
  const c6 = [25.13112622477341, -12.24266895238567, -5.798600375185062];

  const rgb = [0, 1, 2].map(
    (i) =>
      c0[i]! + x * (c1[i]! + x * (c2[i]! + x * (c3[i]! + x * (c4[i]! + x * (c5[i]! + x * c6[i]!)))))
  );

  return [
    Math.round(Math.max(0, Math.min(1, rgb[0]!)) * 255),
    Math.round(Math.max(0, Math.min(1, rgb[1]!)) * 255),
    Math.round(Math.max(0, Math.min(1, rgb[2]!)) * 255),
  ] as [number, number, number];
}

function jetColor(t: number): [number, number, number] {
  if (t == null || isNaN(t)) t = 0;
  const x = Math.max(0, Math.min(1, t));
  const r = Math.max(0, Math.min(1, Math.min(4 * x - 1.5, -4 * x + 4.5)));
  const g = Math.max(0, Math.min(1, Math.min(4 * x - 0.5, -4 * x + 3.5)));
  const b = Math.max(0, Math.min(1, Math.min(4 * x + 0.5, -4 * x + 2.5)));
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)] as [
    number,
    number,
    number,
  ];
}

function grayscaleColor(t: number): [number, number, number] {
  if (t == null || isNaN(t)) {
    t = 0;
  }
  t = Math.max(0, Math.min(1, t));

  // Grayscale: black → white
  const value = Math.round(t * 255);
  return [value, value, value] as [number, number, number];
}

export function colormapToRGB(colorMap: ColorMap, t: number): [number, number, number] {
  switch (colorMap) {
    case ColorMap.VIRIDIS:
      return viridisColor(t);
    case ColorMap.TURBO:
      return turboColor(t);
    case ColorMap.JET:
      return jetColor(t);
    case ColorMap.MAGMA:
      return magmaColor(t);
    case ColorMap.INFERNO:
      return infernoColor(t);
    case ColorMap.GRAYSCALE:
      return grayscaleColor(t);
    default:
      return [0, 0, 0] as [number, number, number];
  }
}

const to255 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);

/**
 * Lift a stop's OKLab lightness toward `floor` (0 = no change), preserving hue
 * and chroma. Mirrors the shader's dark-canvas lift so the DOM gradient matches
 * the rendered mesh. `floor` maps the darkest input (L=0) up to `floor` and
 * leaves the brightest (L=1) unchanged.
 */
function liftLightness(
  [r, g, b]: [number, number, number],
  floor: number
): [number, number, number] {
  if (floor <= 0) return [r, g, b];
  const o = parseToOklch(`rgb(${r}, ${g}, ${b})`);
  const s = oklchToSrgb(clampChroma({ l: floor + o.l * (1 - floor), c: o.c, h: o.h }));
  return [to255(s.r), to255(s.g), to255(s.b)];
}

/**
 * The colour the mesh is drawn in at `position` (0 = lowest hue, 1 = highest) along the
 * theme's colormap domain, as CSS. The shader's `computeSharedColor`, in DOM form.
 */
export function getColormapColor({
  colorMap,
  isDark,
  position,
}: {
  colorMap: ColorMap;
  isDark: boolean;
  position: number;
}): string {
  const { lo, hi } = getColormapDomain(colorMap, isDark);
  const t = lo + position * (hi - lo);
  const [r, g, b] = liftLightness(
    colormapToRGB(colorMap, t),
    getColormapLightFloor(colorMap, isDark)
  );
  return `rgb(${r}, ${g}, ${b})`;
}

export function getColormapGradient(colorMap: ColorMap, isDark: boolean): string {
  const stops: string[] = [];
  const numStops = 10;

  for (let i = 0; i <= numStops; i++) {
    const position = i / numStops;
    const color = getColormapColor({ colorMap, isDark, position });
    stops.push(`${color} ${(position * 100).toFixed(1)}%`);
  }

  return `linear-gradient(to right, ${stops.join(", ")})`;
}
